import React, { useCallback, useContext, useEffect, useState } from "react";
import * as configService from "../../Services/Http/ConfigService";
import * as orderService from "../../Services/Http/OrderService";
import { useOrder } from "../../Context/useOrder";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { Order } from "../../Models/Order";
import { dataContext } from "../../Context/contextProvider";
import { observer } from "mobx-react";
import { Button, Modal } from "react-bootstrap";
import OrderStatusBar from "../../Components/OrderStatusBar/OrderStatusBar";
import tempStore from "../../Store/tempStore";
import { KeyValue } from "../../Models/KeyValue";
import { BoxFill } from "../../Models/BoxFill";
import { TransHeaderResponse } from "../../Models/TransHeaderResponse";
import { SaveBoxListRequest } from "../../Models/SaveBoxListRequest";
import { PrintResponse } from "../../Models/PrintResponse";
import { BooleanResponse } from "../../Models/BooleanResponse";

interface Props {}

const BoxPage = observer((props: Props) => {
  const { getCurrentOrder, setCurrentOrder } = useOrder();
  const [boxList, setBoxList] = useState<KeyValue[]>([]);
  const [filledBoxes, setFilledBoxes] = useState<BoxFill[]>([]);
  const [modal1Open, setModal1Open] = useState<boolean>(false);
  const [modal2Open, setModal2Open] = useState<boolean>(false);
  const [isBoxed, setIsBoxed] = useState<boolean>(false);
  const [isBoxing, setIsBoxing] = useState<boolean>(false);
  const [isInvoiced, setIsInvoiced] = useState<boolean>(false);
  const [isInvoicing, setIsInvoicing] = useState<boolean>(false);
  const [isPrinted, setIsPrinted] = useState<boolean>(false);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);
  const [isShippingUpdated, setIsShippingUpdated] = useState<boolean>(false);
  const [isShippingUpdating, setIsShippingUpdating] = useState<boolean>(false);
  const [invoiceTransRefCode, setInvoiceTransRefCode] = useState<string>("");
  const dataStore = useContext(dataContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (getCurrentOrder() === null) {
      navigate("/orders");
    }
    bindBoxList().then(() => {
      bindBoxesFromOrder();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bindBoxList = async () => {
    try {
      let boxes: KeyValue[] = tempStore.getBoxList(dataStore.serverUrl!, dataStore.dbKey!);
      if (!boxes || boxes.length === 0) {
        boxes = await configService.getBoxListAPI(dataStore.serverUrl!, dataStore.dbKey!);
        tempStore.setBoxList(dataStore.serverUrl!, dataStore.dbKey!, boxes);
      }
      setBoxList(boxes);
    } catch (err) {
      setBoxList([]);
    }
  };

  const bindBoxesFromOrder = async () => {
    const order = getCurrentOrder();
    if (order && order.boxes) {
      setFilledBoxes([...order.boxes]);
    }
  };

  const isFullyPacked = useCallback((order: Order): boolean => {
    if (!order || !order.cartLines) {
      return false;
    }
    return order.cartLines.every((product) => {
      const picked: number = product.qty || 0;
      const packed: number = product.qtyPacked || 0;
      return picked === packed;
    });
  }, []);

  const handleBoxAdded = (box: KeyValue) => {
    setFilledBoxes((prevFilledBoxes) => {
      let arr: BoxFill[] = [];
      if (prevFilledBoxes) {
        arr = [...prevFilledBoxes];
      }
      const additionalBox: BoxFill = {
        index: prevFilledBoxes.length,
        name: box.value || "",
        key: box.key,
        weight: 0,
        weightNotSet: true,
      };
      arr = [...arr, additionalBox];
      return arr;
    });
  };
  const focusNextInput = (currentIndex: number) => {
    const nextInput = document.querySelector(`#box-input-${currentIndex + 1}`);
    if (nextInput) {
      (nextInput as HTMLElement).focus();
    } else {
      handleSubmitBoxes();
    }
  };
  const handleWeightChange = (box: BoxFill, value: string) => {
    const newBox: BoxFill = { ...box, weight: value === "" ? 0 : parseFloat(value), weightNotSet: value === "" ? true : false };
    setFilledBoxes((prevFilledBoxes) => {
      const arr: BoxFill[] = [...prevFilledBoxes];
      const index: number = arr.findIndex((b) => b.index === newBox.index);
      if (index > -1) {
        arr[index] = { ...newBox };
      }
      return arr;
    });
  };
  const handleSubmitBoxes = () => {
    if (!filledBoxes || filledBoxes.length <= 0) {
      toast.error("At least one box must be added");
      return;
    }
    if (!isAllWeightsFilled()) {
      toast.error("Some weights are missing");
      return;
    }
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      return;
    }

    if (!isFullyPacked(order)) {
      setModal1Open(true);
      return;
    }

    onModalYesNo(true);
  };

  const onModalYesNo = (yes: boolean) => {
    setModal1Open(false);
    if (!yes) {
      return;
    }
    openModal2();
  };
  const isAllWeightsFilled = (): boolean => {
    if (filledBoxes.find((b) => b.weightNotSet)) {
      return false;
    }
    return true;
  };

  const openModal2 = async () => {
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      return;
    }
    const _isInvoiced = !!order.isInvoiced;
    const _isPrinted = !!order.isPrinted;
    const _isShippingUpdated = !!order.isShippingUpdated;

    setIsInvoiced(_isInvoiced);
    setIsPrinted(_isPrinted);
    setIsShippingUpdated(_isShippingUpdated);
    try {
      const isBoxedResponse: BooleanResponse = await orderService.isBoxedAPI(order.transRefCode || "", dataStore.serverUrl || "", dataStore.dbKey || "");
      if (isBoxedResponse.hasError) {
        toast.error(isBoxedResponse.errorMessage, { autoClose: false });
        return;
      }
      const _isBoxed = isBoxedResponse.isTrue;
      setIsBoxed(_isBoxed);
      setModal2Open(true);

      if (!_isBoxed && !_isInvoiced && !_isPrinted) {
        const boxesResult: boolean = await handleUpdateBoxes();
        if (boxesResult) {
          const invoiceResult: boolean = await handleInvoiceOrder();
          if (invoiceResult) {
            const shippingResult: boolean = await handleUpdateShipping();
            if (shippingResult) {
              const printResult: boolean = await handlePrint();
              if (printResult) {
                setCurrentOrder(null);
                navigate("/packlist");
              }
            }
          }
        }
      }
    } catch (error: any) {
      setIsBoxed(false);
      toast.error(error.message || error, { autoClose: false });
    }
  };
  const onCloseModal1 = () => {
    setModal1Open(false);
  };

  const onCloseModal2 = () => {
    setModal2Open(false);
  };

  const handleUpdateBoxes = async (): Promise<boolean> => {
    setIsBoxing(true);
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      setIsBoxing(false);
      return false;
    }

    let result: boolean = false;
    const model: SaveBoxListRequest = {
      transRefCode: order.transRefCode,
      transNo: order.transNo,
      orderCompanyNo: order.companyNo,
      boxes: filledBoxes,
    };
    try {
      const resp: TransHeaderResponse = await orderService.saveBoxListAPI(model, dataStore.serverUrl || "", dataStore.dbKey || "");
      if (resp.hasError) {
        toast.error(resp.errorMessage, { autoClose: false });
        setIsBoxed(false);
        result = false;
      } else {
        toast.success("Boxes updated successfully");
        setIsBoxed(true);
        result = true;
        //setCurrentOrder(null);
        //navigate("/packlist");
      }
    } catch (error: any) {
      setIsBoxed(false);
      toast.error(error.message || error, { autoClose: false });
      result = false;
    } finally {
      setIsBoxing(false);
    }
    return result;
  };

  const handleInvoiceOrder = async (): Promise<boolean> => {
    if (isInvoiced) {
      toast.error("Invoice already created");
      return false;
    }
    setIsInvoicing(true);
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      setIsInvoicing(false);
      return false;
    }
    let result: boolean = false;
    try {
      const resp: TransHeaderResponse = await orderService.invoiceOrderAPI(order.transRefCode || "", dataStore.serverUrl || "", dataStore.dbKey || "");
      if (resp.hasError) {
        toast.error(resp.errorMessage, { autoClose: false });
        setIsInvoiced(false);
        result = false;
      } else {
        toast.success("Invoice created successfully");
        setIsInvoiced(true);
        setInvoiceTransRefCode(resp.transRefCode || "");
        result = true;
        //setCurrentOrder(null);
        //navigate("/packlist");
      }
    } catch (error: any) {
      setIsInvoiced(false);
      toast.error(error.message || error, { autoClose: false });
      result = false;
    } finally {
      setIsInvoicing(false);
    }
    return result;
  };

  const handleUpdateShipping = async (): Promise<boolean> => {
    setIsShippingUpdating(true);
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      setIsShippingUpdating(false);
      return false;
    }
    let result: boolean = false;
    try {
      const resp: TransHeaderResponse = await orderService.updateShippingAPI(order.transRefCode || "", dataStore.serverUrl || "", dataStore.dbKey || "");
      if (resp.hasError) {
        toast.error(resp.errorMessage, { autoClose: false });
        setIsShippingUpdated(false);
        result = false;
      } else {
        toast.success("Shipping updated successfully");
        setIsShippingUpdated(true);
        result = true;
      }
    } catch (error: any) {
      setIsShippingUpdated(false);
      toast.error(error.message || error, { autoClose: false });
      result = false;
    } finally {
      setIsShippingUpdating(false);
    }
    return result;
  };

  const handlePrint = async (): Promise<boolean> => {
    setIsPrinting(true);
    const order = getCurrentOrder();
    if (!order) {
      toast.error("Order not found");
      setIsPrinting(false);
      return false;
    }
    let result: boolean = false;
    try {
      const resp: PrintResponse = await orderService.printAPI(order.transRefCode || "", dataStore.serverUrl || "", dataStore.dbKey || "");

      if (resp.hasError) {
        toast.error(`Printing error: ${resp.description}`, { autoClose: false });
        result = false;
      } else {
        toast.success("Sent to printer");
        result = true;
      }
      setIsPrinted(!resp.hasError);
    } catch (error: any) {
      setIsPrinted(false);
      toast.error(error.message || error, { autoClose: false });
      result = false;
    } finally {
      setIsPrinting(false);
    }
    return result;
  };
  const onInvoiceProcessed = () => {
    setModal2Open(false);
    setCurrentOrder(null);
    navigate("/packlist");
  };
  return (
    <>
      <OrderStatusBar allowPrint={false} />
      {boxList && boxList.length > 0 && (
        <div className="row">
          {boxList.map((box, index) => {
            return (
              <div key={index} className="col-3 d-flex align-content-stretch m-0 p-0">
                <Button className="flex-fill m-1" key={index} variant="warning" onClick={() => handleBoxAdded(box)}>
                  {box.value}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <div className="container-limited-width order-pack-page">
        <div className="row">
          <div className="col-12 mb-2 mt-2 p-0">
            <ul className="list-group list-group-flush p-0">
              {filledBoxes &&
                filledBoxes.length > 0 &&
                filledBoxes.map((box, index) => {
                  return (
                    <li id={box.key} key={index} className="list-group-item d-flex align-items-center p-1 product-list-item">
                      <div className="input-group">
                        <div className="col-3 d-flex justify-content-center">
                          <label className="col-form-label">{box.name}</label>
                        </div>
                        <div className="col-8">
                          <input
                            id={`box-input-${index}`}
                            type="number"
                            min="0"
                            className="form-control"
                            autoComplete="chrome-off"
                            value={box.weightNotSet ? "" : box.weight}
                            onChange={(e) => {
                              handleWeightChange(box, e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                focusNextInput(index);
                              }
                            }}
                          />
                        </div>
                        <div className="col-1 d-flex justify-content-end">
                          <Button
                            variant="link"
                            className="text-danger"
                            onClick={() => {
                              setFilledBoxes((prevFilledBoxes) => prevFilledBoxes.filter((b) => b.index !== box.index));
                            }}
                          >
                            <i className="fa fa-trash"></i>
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex justify-content-center">
            <span className="fw-bold">Box Count</span>
            <span className="fw-bold ms-2">{filledBoxes.length}</span>
          </div>
        </div>
        <div className="row">
          <div className="col-12 d-flex mt-2 mb-4">
            <Button className="flex-fill m-1" variant="success" onClick={handleSubmitBoxes}>
              Submit
            </Button>
          </div>
        </div>
      </div>
      <Modal show={modal1Open} onHide={onCloseModal1} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>Order is not fully packed. Continue ?</Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={() => onModalYesNo(true)}>
            Yes
          </Button>
          <Button variant="danger" onClick={() => onModalYesNo(false)}>
            No
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={modal2Open} onHide={onCloseModal2} backdrop="static" keyboard={false} centered>
        <Modal.Header closeButton={true}>
          <Modal.Title>Actions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-4">
            <div className="col-2 d-flex justify-content-end align-items-center">
              {isBoxing ? <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> : isBoxed ? <i className="fa fa-check icon-success" aria-hidden="true"></i> : null}
            </div>
            <div className="col-9 d-flex p-0">
              <Button variant="warning" className="flex-fill" onClick={handleUpdateBoxes} disabled={isBoxing}>
                Update Boxes / Print Labels
              </Button>
            </div>
          </div>

          <div className="row my-4">
            <div className="col-2 d-flex justify-content-end align-items-center">
              {isInvoicing ? <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> : isInvoiced ? <i className="fa fa-check icon-success" aria-hidden="true"></i> : null}
            </div>
            <div className="col-9 d-flex p-0">
              <Button variant="warning" className="flex-fill" onClick={handleInvoiceOrder} disabled={isInvoicing}>
                Invoice Order
              </Button>
            </div>
          </div>

          <div className="row my-4">
            <div className="col-2 d-flex justify-content-end align-items-center">
              {isShippingUpdating ? <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> : isShippingUpdated ? <i className="fa fa-check icon-success" aria-hidden="true"></i> : null}
            </div>
            <div className="col-9 d-flex p-0">
              <Button variant="warning" className="flex-fill" onClick={handleUpdateShipping} disabled={isShippingUpdating}>
                Update Shipping / Freight Labels
              </Button>
            </div>
          </div>

          <div className="row mt-4 mb-2">
            <div className="col-2 d-flex justify-content-end align-items-center">
              {isPrinting ? <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> : isPrinted ? <i className="fa fa-check icon-success" aria-hidden="true"></i> : null}
            </div>
            <div className="col-9 d-flex p-0">
              <Button variant="warning" className="flex-fill" onClick={handlePrint} disabled={isPrinting}>
                Print Invoice
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {!isBoxing && isBoxed && !isInvoicing && isInvoiced && !isShippingUpdating && isShippingUpdated && !isPrinting && isPrinted && (
            <Button className="flex-fill m-1" variant="success" onClick={() => onInvoiceProcessed()}>
              Invoice Processed {invoiceTransRefCode || getCurrentOrder()?.lastActionRefCode}
              <br />
              Click to close
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
});

export default BoxPage;
